/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import {nullify, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {DataTablePageEvent, DataTableRowClickEvent, DataTableStateEvent} from 'primereact/datatable';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {BolReviewTableSorting, OrderDirection, ReviewSalesPeriod, ReviewTableProduct} from '../../../util/interfaces';
import {getReviewProducts} from '../../../util/requestSender';
import {AppContext} from '../../misc/appContext';
import {createErrorMessage} from '../../../util/utils';
import {useNavigate} from 'react-router';
import ReviewsTable from './reviewsTable';
import {ISO_DATE_FORMAT} from '../../../util/constants';
import {getReviewAnalyticsMem, setReviewAnalyticsMemValue} from '../../../util/reviewAnalyticsUtil';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

type ReviewsLoadConfig = {
    search?: string | null;
    limit?: number | null;
    page?: number;
    sortField?: string | null;
    sortOrder?: number | null;
};
const SEARCH_DEBOUNCE = 500;

function sortingFromSortField(sortField: string): BolReviewTableSorting {
    if (sortField === 'title' || !sortField.length) return BolReviewTableSorting.TITLE;
    else if (sortField === 'total') return BolReviewTableSorting.TOTAL;
    else if (sortField === 'total_positive') return BolReviewTableSorting.POSITIVE;
    else if (sortField === 'total_negative') return BolReviewTableSorting.NEGATIVE;
    else if (sortField === 'period_total') return BolReviewTableSorting.PERIOD_TOTAL;
    else if (sortField === 'period_positive') return BolReviewTableSorting.PERIOD_POSITIVE;
    else if (sortField === 'period_negative') return BolReviewTableSorting.PERIOD_NEGATIVE;
    else if (sortField === 'rating') return BolReviewTableSorting.SCORE_RATING;
    else if (sortField === 'nps_score') return BolReviewTableSorting.NPS_SCORE;
    else if (sortField === 'period_nps_score') return BolReviewTableSorting.PERIOD_NPS_SCORE;
    throw Error(`Unknown sort field ${sortField}`);
}

function orderDirectionFromInt(orderDirection: number | null | undefined): OrderDirection {
    if (orderDirection === null || orderDirection === undefined || orderDirection < 1) return OrderDirection.DESC;
    return OrderDirection.ASC;
}

interface Props {
    period: ReviewSalesPeriod;
    fromDate: IDate | null;
    toDate: IDate | null;
}

export default function ReviewsTableController(props: Props) {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const {period, toDate, fromDate} = props;
    const [products, setProducts] = useState<Array<ReviewTableProduct>>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState('period_total');
    const [sortOrder, setSortOrder] = useState<0 | 1 | -1 | null | undefined>(-1);
    const [search, setSearch] = useState('');
    const [firstRow, setFirstRow] = useState(0);
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);
    const [mounted, setMounted] = useState(false);
    const searchDebounce = useRef<number | null>(null);
    const navigate = useNavigate();

    function onClickRow(evt: DataTableRowClickEvent) {
        navigate(`/review-analytics/${evt.data.ean}`);
    }

    function onClickCell(ean: string, clickType: 'positive' | 'negative') {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            navigate(`/review-analytics/${ean}?rating=${clickType}`);
        };
    }

    function onSort(evt: DataTableStateEvent) {
        const sortField = evt.sortField;
        const sortOrder = evt.sortOrder;
        setSortField(sortField);
        setSortOrder(sortOrder);
        loadProducts({sortField, sortOrder});
        setReviewAnalyticsMemValue('tableSort', sortField);
        setReviewAnalyticsMemValue('tableSortOrder', sortOrder);
    }

    function onChangeSearch(evt: React.ChangeEvent<HTMLInputElement>) {
        clearDebounce();
        const value = evt.target.value;
        setSearch(value);
        searchDebounce.current = window.setTimeout(() => {
            setReviewAnalyticsMemValue('tableSearch', value);
            setPage(0);
            setFirstRow(0);
            loadProducts({page: 0, search: value});
        }, SEARCH_DEBOUNCE);
    }

    function onChangePage(evt: DataTablePageEvent) {
        if (!mounted) return;
        if (evt.page !== undefined) {
            setPage(evt.page);
            setReviewAnalyticsMemValue('tablePage', evt.page);
        }
        setLimit(evt.rows);
        setFirstRow(evt.first);
        loadProducts({page: evt.page, limit: evt.rows});
    }

    function loadProducts(config?: ReviewsLoadConfig) {
        const {account} = context.accountContext;
        if (!account) return;
        setLoading(true);
        const _search = nullify(config?.search ?? search);
        const _page = config?.page ?? page;
        const _limit = config?.limit ?? limit;
        const _sorting = sortingFromSortField(config?.sortField ?? sortField);
        const _orderDirection = orderDirectionFromInt(config?.sortOrder ?? sortOrder);
        getReviewProducts({
            data: {
                limit: _limit,
                page: _page,
                search: _search,
                account_id: account.id,
                order_direction: _orderDirection,
                sorting: _sorting,
                period,
                from_date: fromDate?.format(ISO_DATE_FORMAT) ?? null,
                to_date: toDate?.format(ISO_DATE_FORMAT) ?? null
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    setProducts(data.products);
                    setTotal(data.total);
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'reviewAnalytics.reviewsTable.loadProductsFailed',
                                    'Het laden van de producten is niet gelukt.'
                                )
                        ),
                        t('reviewAnalytics.reviewsTable.loadFailed', 'Laden mislukt')
                    );
            }
        });
    }

    function clearDebounce() {
        if (searchDebounce.current !== null) {
            window.clearTimeout(searchDebounce.current);
            searchDebounce.current = null;
        }
    }

    function setDefaults() {
        // Load the default settings from memory
        const memory = getReviewAnalyticsMem();
        const page = memory.tablePage;
        const search = memory.tableSearch;
        const _limit = memory.tableLimit;
        const sort = memory.tableSort;
        const order = memory.tableSortOrder;
        if (_limit) setLimit(_limit);
        if (page) {
            setFirstRow(page * (_limit ?? limit));
            setPage(page);
        }
        if (search) setSearch(search);
        if (sort) setSortField(sort);
        if (order) setSortOrder(order as 0 | 1 | -1);
        loadProducts({page, search, limit: _limit, sortField: sort, sortOrder: order});
    }

    useEffect(() => {
        if (mounted) loadProducts();
        return function () {
            clearDebounce();
        };
    }, [period, fromDate, toDate, context.accountContext.account]);

    useEffect(() => {
        setDefaults();
        setMounted(true);
    }, []);

    return (
        <ReviewsTable
            onSort={onSort}
            onChangeSearch={onChangeSearch}
            onChangePage={onChangePage}
            onClickRow={onClickRow}
            onClickCell={onClickCell}
            products={products}
            total={total}
            firstRow={firstRow}
            loading={loading}
            sortField={sortField}
            sortOrder={sortOrder}
            search={search}
            limit={limit}
            fromDate={fromDate}
            toDate={toDate}
        />
    );
}
